import { RootSchema } from "../../redux/reducers";
import { AnalyticsEvent, AnalyticsProperties } from "./types";

const DEFAULT_SOURCE = "admin_console";
const MAX_STRING_LENGTH = 300;
const SENSITIVE_KEY_PATTERN = /(secret|token|certificate|password|key|private|authorization)/i;

const getPathname = () => {
  if (typeof window === "undefined") {
    return "";
  }

  return window.location.pathname || "";
};

const getScreen = (pathname: string) => {
  const segments = pathname.split("/").filter(Boolean);
  return segments.join("_") || "root";
};

const cleanString = (value: string) => value.trim().slice(0, MAX_STRING_LENGTH);

const sanitizeValue = (key: string, value: unknown, forError = false): unknown => {
  if (value === undefined || value === null || typeof value === "function") {
    return undefined;
  }

  if (forError && SENSITIVE_KEY_PATTERN.test(key)) {
    return undefined;
  }

  if (typeof value === "string") {
    return cleanString(value);
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeValue(key, item, forError))
      .filter((item) => item !== undefined)
      .slice(0, 20);
  }

  if (!forError && typeof value === "object") {
    return value;
  }

  return undefined;
};

const buildBaseProps = (properties: AnalyticsProperties, state?: RootSchema): AnalyticsProperties => {
  const pathname = typeof properties.pathname === "string" ? properties.pathname : getPathname();
  const currentAppInfo = (properties.currentAppInfo as { AppId?: string | number; AppName?: string } | undefined) || state?.auth?.currentAppinfo;
  const localStorageAppName = typeof window !== "undefined" ? localStorage.getItem("siteName") || undefined : undefined;

  return {
    pathname,
    screen: typeof properties.screen === "string" ? properties.screen : getScreen(pathname),
    app_id: (properties.app_id as string | number | undefined) || currentAppInfo?.AppId,
    app_name: (properties.app_name as string | undefined) || currentAppInfo?.AppName || localStorageAppName,
    source: typeof properties.source === "string" ? properties.source : DEFAULT_SOURCE
  };
};

export const buildAnalyticsEvent = (event: AnalyticsEvent, state?: RootSchema): AnalyticsEvent => {
  const properties = event.properties || {};
  const { currentAppInfo: _currentAppInfo, ...rest } = properties;
  const payload: AnalyticsProperties = { ...buildBaseProps(properties, state) };

  Object.entries(rest).forEach(([key, value]) => {
    const sanitized = sanitizeValue(key, value, event.forError);
    if (sanitized !== undefined) {
      payload[key] = sanitized;
    }
  });

  return {
    ...event,
    properties: payload
  };
};

export const sanitizeAnalyticsProperties = (properties: AnalyticsProperties, forError = false): AnalyticsProperties => {
  const payload: AnalyticsProperties = {};

  Object.entries(properties).forEach(([key, value]) => {
    const sanitized = sanitizeValue(key, value, forError);
    if (sanitized !== undefined) {
      payload[key] = sanitized;
    }
  });

  return payload;
};
